import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash, Eye, Printer } from "@phosphor-icons/react";
import { Modal, Field } from "./Suppliers";
import { toast } from "sonner";

export default function GRNs() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const canDelete = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => setItems((await api.get("/grns")).data);

  useEffect(() => {
    (async () => {
      const [g, s, p] = await Promise.all([api.get("/grns"), api.get("/suppliers"), api.get("/parts")]);
      setItems(g.data); setSuppliers(s.data); setParts(p.data);
    })();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this GRN? (Only allowed if nothing consumed)")) return;
    try { await api.delete(`/grns/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="grns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Goods Receipt Notes</h1>
          <p className="text-xs text-zinc-600 mt-1">Inward stock tied to vendor invoice</p>
        </div>
        {canEdit && (
          <button onClick={() => setOpen(true)} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="new-grn-btn">
            <Plus size={14} weight="bold" /> New GRN
          </button>
        )}
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="brut-table" data-testid="grns-table">
          <thead>
            <tr><th>GRN #</th><th>Supplier</th><th>Invoice</th><th>Received</th><th>Lines</th><th>Total Qty</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500">No GRNs yet.</td></tr>}
            {items.map((g) => {
              const totalQty = (g.lines || []).reduce((a, b) => a + (b.qty || 0), 0);
              return (
                <tr key={g.id} data-testid={`grn-row-${g.grn_number}`}>
                  <td className="font-bold">{g.grn_number}</td>
                  <td>{g.supplier_name}</td>
                  <td>{g.invoice_number}</td>
                  <td>{g.received_date}</td>
                  <td>{g.lines?.length || 0}</td>
                  <td className="font-mono-ui">{totalQty}</td>
                  <td className="text-right">
                    <Link to={`/grns/${g.id}`} className="brut-tag brut-tag-yellow mr-1" data-testid={`view-grn-${g.grn_number}`}>
                      <Eye size={10} weight="bold" />
                    </Link>
                    {canDelete && (
                      <button onClick={() => remove(g.id)} className="brut-tag brut-tag-red" data-testid={`delete-grn-${g.grn_number}`}>
                        <Trash size={10} weight="bold" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <NewGRN
          suppliers={suppliers}
          parts={parts}
          onClose={() => setOpen(false)}
          onSaved={(g) => { setOpen(false); load(); navigate(`/grns/${g.id}`); }}
        />
      )}
    </div>
  );
}

function NewGRN({ suppliers, parts, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    supplier_id: suppliers[0]?.id || "",
    invoice_number: "",
    invoice_date: today,
    received_date: today,
    remarks: "",
  });
  const [lines, setLines] = useState([{ part_id: "", qty: 0, remarks: "" }]);

  // Parts allowed for the currently selected supplier:
  // 1) non-assembled
  // 2) supplier approved (or supplier is INTERNAL and part is_internal)
  const supplier = suppliers.find((s) => s.id === form.supplier_id);
  const isInternalSup = supplier?.code === "INT";
  const allowedParts = parts.filter((p) => {
    if (p.is_assembled) return false;
    if (isInternalSup) return !!p.is_internal;
    return (p.approved_supplier_ids || []).includes(form.supplier_id);
  });

  const addLine = () => setLines([...lines, { part_id: allowedParts[0]?.id || "", qty: 0, remarks: "" }]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const updateLine = (i, key, val) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [key]: val } : l)));

  const save = async (e) => {
    e.preventDefault();
    if (!form.supplier_id) return toast.error("Select a supplier");
    if (!lines.length) return toast.error("Add at least one line");
    if (lines.some((l) => !l.part_id || !l.qty || l.qty <= 0))
      return toast.error("Each line needs a part and qty > 0");

    try {
      const { data } = await api.post("/grns", { ...form, lines });
      toast.success(`GRN ${data.grn_number} created`);
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Modal title="New GRN" onClose={onClose}>
      {suppliers.length === 0 || parts.length === 0 ? (
        <div className="brut-tag brut-tag-red w-full text-center" data-testid="grn-prereq-warning">
          You need at least 1 supplier and 1 part before creating a GRN.
        </div>
      ) : (
        <form onSubmit={save} className="space-y-4" data-testid="grn-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="brut-label">Supplier *</label>
              <select required value={form.supplier_id} onChange={(e) => { setForm({ ...form, supplier_id: e.target.value }); setLines([{ part_id: "", qty: 0, remarks: "" }]); }} className="brut-input mt-1" data-testid="grn-supplier">
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.code} · {s.name}</option>)}
              </select>
              {isInternalSup && <div className="text-[10px] text-zinc-600 mt-1">Internal production · only internally-manufactured parts shown.</div>}
            </div>
            <Field label="Invoice / Reference #" required value={form.invoice_number} onChange={(v) => setForm({ ...form, invoice_number: v })} testid="grn-invoice" />
            <Field label="Invoice Date" type="date" required value={form.invoice_date} onChange={(v) => setForm({ ...form, invoice_date: v })} testid="grn-invoice-date" />
            <Field label="Received Date" type="date" required value={form.received_date} onChange={(v) => setForm({ ...form, received_date: v })} testid="grn-received-date" />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="brut-label">Line Items</div>
              <button type="button" onClick={addLine} disabled={allowedParts.length === 0} className="brut-tag brut-tag-dark inline-flex items-center gap-1 disabled:opacity-50" data-testid="add-line-btn">
                <Plus size={10} weight="bold" /> Line
              </button>
            </div>
            {allowedParts.length === 0 && (
              <div className="brut-tag brut-tag-red w-full text-center mb-2" data-testid="no-approved-parts">
                No parts are approved for this supplier. Add approved suppliers on the Parts page first.
              </div>
            )}
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end brut-border p-2" data-testid={`grn-line-${i}`}>
                  <div className="col-span-7">
                    <label className="brut-label">Part</label>
                    <select value={l.part_id} onChange={(e) => updateLine(i, "part_id", e.target.value)} className="brut-input mt-1" data-testid={`grn-line-part-${i}`}>
                      <option value="">— Select —</option>
                      {allowedParts.map((p) => <option key={p.id} value={p.id}>{p.part_number} · {p.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="brut-label">Qty</label>
                    <input type="number" min="0" step="any" value={l.qty} onChange={(e) => updateLine(i, "qty", parseFloat(e.target.value) || 0)} className="brut-input mt-1" data-testid={`grn-line-qty-${i}`} />
                  </div>
                  <div className="col-span-2">
                    <label className="brut-label">Remarks</label>
                    <input value={l.remarks} onChange={(e) => updateLine(i, "remarks", e.target.value)} className="brut-input mt-1" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button type="button" onClick={() => removeLine(i)} className="brut-tag brut-tag-red" data-testid={`remove-line-${i}`}>
                      <Trash size={10} weight="bold" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Field label="Remarks" value={form.remarks} onChange={(v) => setForm({ ...form, remarks: v })} textarea testid="grn-remarks" />

          <button type="submit" className="brut-btn brut-btn-primary px-4 py-2" data-testid="save-grn-btn">Create GRN</button>
        </form>
      )}
    </Modal>
  );
}
